import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Heart, Target, Server, HandCoins, MapPin, Copy, Check } from "lucide-react";
import { LogoMark } from "./Logo";

const PAYPAL_URL = "https://www.paypal.me/leonardocaste";
const GOAL = 500;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lines = [
  { icon: Heart, text: "Stiamo costruendo la prima app community per cani in Italia." },
  { icon: Server, text: "Ogni donazione copre i costi server e ci permette di restare gratuiti per tutti gli utenti." },
  { icon: Target, text: "Obiettivo: 500 utenti attivi entro dicembre 2026." },
];

export const Support = () => {
  const [count, setCount] = useState(null);
  const [cities, setCities] = useState([]);
  const [copied, setCopied] = useState(false);

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText("LU67 8100 SATI 9361 2617");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  useEffect(() => {
    const load = () => {
      axios.get(`${API}/waitlist/count`).then((r) => setCount(r.data.count)).catch(() => {});
      axios.get(`${API}/waitlist/cities`).then((r) => setCities(r.data.cities || [])).catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const pct = count === null ? 0 : Math.min(100, (count / GOAL) * 100);

  return (
    <section
      data-testid="support-section"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-36 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[44rem] h-[24rem] rounded-full bg-[#FF6B35]/[0.08] blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto text-center flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <LogoMark className="w-14 h-14" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#FF6B35]"
        >
          Supporta FuffyFam
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="support-title"
          className="font-display font-extrabold mt-4 text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A1A1A]"
        >
          Aiutaci a portare FuffyFam a <span className="text-[#FF6B35]">500 cani a Torino</span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 w-full max-w-xl"
          data-testid="support-progress"
        >
          <div className="flex items-end justify-between mb-3">
            <p data-testid="support-progress-label" className="font-display font-bold text-2xl sm:text-3xl text-[#1A1A1A]">
              {count === null ? "…" : count} <span className="text-[#5C5C5C] font-medium text-lg sm:text-xl">di {GOAL} cani</span>
            </p>
            <p className="text-xs sm:text-sm font-semibold text-[#FF6B35]">{pct.toFixed(1)}%</p>
          </div>
          <div className="h-4 w-full bg-[#1A1A1A]/[0.06] rounded-full overflow-hidden">
            <motion.div
              data-testid="support-progress-bar"
              className="h-full bg-[#FF6B35] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
          {cities.length > 0 && (
            <p
              data-testid="support-cities"
              className="mt-4 flex items-center justify-center gap-2 text-sm text-[#5C5C5C]"
            >
              <MapPin className="w-4 h-4 text-[#FF6B35] shrink-0" />
              Si sono già iscritti da {cities.join(", ")}
            </p>
          )}
        </motion.div>

        <motion.ul
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 space-y-5 text-left max-w-xl w-full"
        >
          {lines.map(({ icon: Icon, text }, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FF6B35]/10 shrink-0 mt-0.5">
                <Icon className="w-5 h-5 text-[#FF6B35]" />
              </span>
              <p className="text-base sm:text-lg leading-relaxed text-[#5C5C5C]">{text}</p>
            </li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-col items-center"
        >
          <div className="grid sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <motion.a
              href={PAYPAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="support-paypal-button"
              className="flex flex-col items-center justify-center gap-2.5 bg-[#FF6B35] hover:bg-[#E55A2B] text-white rounded-3xl px-8 py-7 shadow-[0_16px_50px_rgba(255,107,53,0.35)] transition-colors duration-300"
            >
              <span className="flex items-center gap-2.5 font-bold text-base sm:text-lg">
                <HandCoins className="w-5 h-5" />
                Dona con PayPal
              </span>
              <span className="text-xs text-white/75 font-medium">paypal.me/leonardocaste · si apre in una nuova scheda</span>
            </motion.a>

            <div
              data-testid="support-satispay-box"
              className="flex flex-col items-start gap-2 bg-white border border-[#1A1A1A]/10 rounded-3xl px-6 py-6 text-left shadow-[0_16px_50px_rgba(26,26,26,0.06)]"
            >
              <p className="font-display font-bold text-base sm:text-lg text-[#1A1A1A]">
                Dona con <span className="text-[#FF3D2E]">Satispay</span>
              </p>
              <p className="text-sm text-[#5C5C5C]">
                Beneficiario: <span className="font-semibold text-[#1A1A1A]">Leonardo Castellaro</span>
              </p>
              <div className="flex items-center gap-2 w-full bg-[#F9F8F6] border border-[#1A1A1A]/[0.08] rounded-xl px-3.5 py-2.5">
                <code data-testid="support-satispay-iban" className="flex-1 font-mono text-sm font-semibold text-[#1A1A1A] tracking-wide">
                  LU67 8100 SATI 9361 2617
                </code>
                <motion.button
                  type="button"
                  onClick={copyIban}
                  whileTap={{ scale: 0.9 }}
                  data-testid="support-satispay-copy"
                  aria-label="Copia IBAN"
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FF6B35]/10 hover:bg-[#FF6B35] text-[#FF6B35] hover:text-white transition-colors duration-300 shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </motion.button>
              </div>
              <p className="text-xs sm:text-sm text-[#5C5C5C] leading-relaxed">
                Apri Satispay, tocca Invia, cerca per IBAN e inserisci l'importo che vuoi.
              </p>
            </div>
          </div>
          <p data-testid="support-note" className="mt-4 text-xs sm:text-sm text-[#5C5C5C]">
            Ogni importo è benvenuto. Anche 1€ conta.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
